import React, { useEffect, useRef, useState } from 'react';
import Button from 'components/ui/Button/Button';
import FloatingLabelInput from 'components/ui/Inputs/FloatingLabelInput';
import { usePageContext } from 'context/hooks/usePageContext';
import {
  checkResponse,
  formatCurrency,
  getResponseMessage,
  isErrorBody,
} from 'utils';
import { CardFormValues, validateCardForm } from 'utils/validation';
import axiosInstance from 'utils/axiosInstance';
import { useTransactionContext } from 'context/hooks/useTransactionContext';
import useGetCardPaymentStatus from 'hooks/useGetCardPaymentStatus';
import { PAGE_SUB } from 'context/pageContext';
import { SpinnerInline } from 'components/ui/Spinner';
import { PAYMENT_PROVIDER } from 'types/enums';
import { isCustomsProvider, resolveProvider } from 'config/providers';
import CardNumberInput from 'components/ui/Inputs/CardNumberInput';
import { useFormik } from 'formik';
import Icon from 'components/ui/Icon/Icon';
import { CardData, PaymentHandlerFactory } from 'services/paymentHandlers';
import { getApiResponseMessage } from 'services/paymentHandlers/apiError';

import { ReactComponent as Spinner } from '../../../assets/icons/spinner.svg';
import { useCardContext } from 'context/hooks/useCardContext';
import { usePayableAmount } from 'context/hooks/usePayableAmount';
// TEMP: amount/fee breakdown hidden for now — re-enable to show it again
// import FeeBreakdown from 'components/ui/FeeBreakdown';

const CardComponent = ({
  amount,
  email,
  setUrl,
}: {
  amount: number;
  email?: string;
  setUrl: React.Dispatch<React.SetStateAction<string>>;
}) => {
  const { changeSub, changePage } = usePageContext();
  const { changeCardDetails } = useCardContext();
  const {
    ceviantTransactionId,
    merchantCollectionPublicKey,
    provider,
    selectProvider,
  } = useTransactionContext();
  const [shouldFetch, setShouldFetch] = useState(false);
  const [cardBrand, setCardBrand] = useState<string | null>(null);
  const { triggerFetch, error: statusError } = useGetCardPaymentStatus(
    ceviantTransactionId,
    { shouldFetch },
  );
  const binFetchedRef = useRef<string | null>(null);

  const payable = usePayableAmount('CARD');

  // the status check after a failed charge could not be made either
  useEffect(() => {
    if (statusError) {
      changeSub(PAGE_SUB.ERROR);
    }
  }, [statusError, changeSub]);

  const formik = useFormik<CardFormValues>({
    initialValues: {
      cardNumber: '',
      expirationDate: '',
      cvv: '',
    },
    validate: (values) => validateCardForm(values).errors,
    onSubmit: async (values) => {
      changeCardDetails(values);

      try {
        const activeProvider = provider as PAYMENT_PROVIDER;

        if (activeProvider !== PAYMENT_PROVIDER.PAYSTACK) {
          changeSub(PAGE_SUB.PIN_AUTH);
          return;
        }

        const handler = PaymentHandlerFactory.card(activeProvider);
        const cardData: CardData = {
          cardNumber: values.cardNumber.replace(/\s+/g, ''),
          expirationDate: values.expirationDate,
          cvv: values.cvv,
        };
        const encryptedCardDetails = await handler.encryptCardData(
          cardData,
          merchantCollectionPublicKey,
        );

        const payload = {
          transactionId: ceviantTransactionId,
          email,
          amount,
          card: encryptedCardDetails,
          scheme: cardBrand?.toLowerCase() || 'unknown',
        };

        const response = await handler.processPayment(payload);

        if (isErrorBody(response)) {
          changeSub(PAGE_SUB.ERROR, getResponseMessage(response));
          return;
        }

        if (response) {
          if (response.url) {
            setUrl(response.url);
          }
          changeCardDetails({
            otpPrompt: response.metadata?.displayText ?? '',
          });
          checkResponse(
            response.response,
            changeSub,
            changePage,
            getResponseMessage(response),
          );
        }
      } catch (error) {
        // The API rejected the charge and said why (e.g. "Card declined"),
        // so show that rather than polling a status that hides it.
        const apiMessage = getApiResponseMessage(error);
        if (apiMessage) {
          changeSub(PAGE_SUB.ERROR, apiMessage);
          return;
        }

        setShouldFetch(true);
        triggerFetch();
      }
    },
  });

  const handleCardChange = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;

    if (name === 'cvv') {
      const sanitized = value.replace(/\D/g, '').slice(0, 4);
      formik.setFieldValue('cvv', sanitized);
      return;
    }

    const digits = value.replace(/\D/g, '').slice(0, 19);
    formik.setFieldValue(name as keyof CardFormValues, digits);

    const bin = digits.slice(0, 6);

    // The BIN was edited, so the brand from the previous lookup is stale.
    if (binFetchedRef.current && binFetchedRef.current !== bin) {
      binFetchedRef.current = null;
      setCardBrand(null);
      changeCardDetails({ cardBrand: '' });
    }

    if (bin.length === 6 && binFetchedRef.current !== bin) {
      binFetchedRef.current = bin;

      try {
        const response = await axiosInstance.get(`/payments/bin/${bin}`);
        // Ignore a response for a BIN the user has since changed.
        if (binFetchedRef.current !== bin) return;
        const { countryCode, currency, brand } = response.data;

        const binProvider =
          countryCode === 'NG' && currency === 'NGN'
            ? PAYMENT_PROVIDER.PAYSTACK
            : PAYMENT_PROVIDER.PAYLOFT;

        setCardBrand(brand);
        changeCardDetails({ cardBrand: brand ?? '' });

        // A customs payment is locked to FirstCheckout; the lookup only
        // supplies the card brand.
        if (!isCustomsProvider(provider)) {
          selectProvider(resolveProvider(binProvider));
        }
      } catch (error) {
        if (binFetchedRef.current !== bin) return;
        console.error('Error fetching BIN details:', error);
        binFetchedRef.current = null;
        changeCardDetails({ cardBrand: '' });
      }
    }
  };

  const handleExpirationDateChange = (
    e: React.ChangeEvent<HTMLInputElement>,
  ) => {
    const digits = e.target.value.replace(/\D/g, '').slice(0, 4);
    const value =
      digits.length >= 3 ? `${digits.slice(0, 2)}/${digits.slice(2)}` : digits;
    formik.setFieldValue('expirationDate', value);
  };

  if (!ceviantTransactionId) {
    return <SpinnerInline />;
  }

  return (
    <>
      <div className="text-center p-5 bg-[#C9E5FF] rounded-sm w-full max-w-xs m-auto max-lg:hidden">
        {/* <p>You are about to pay:</p> */}
        <span className="font-bold text-2xl text-[#0284F8] pt-1">
          {formatCurrency(payable.displayTotal / 100, 'NGN')}
          {/* TEMP: amount/fee breakdown hidden for now — re-enable to show it again
          <FeeBreakdown
            baseAmount={payable.baseAmount}
            fee={payable.displayFee}
            className="hidden lg:block"
          /> */}
        </span>
      </div>
      <h4 className="text-center">Enter your card details to pay</h4>
      <form
        onSubmit={formik.handleSubmit}
        className="space-y-3 px-4 md:px-0"
      >
        <div>
          <CardNumberInput
            name="cardNumber"
            label="CARD NUMBER"
            value={formik.values.cardNumber}
            onChange={handleCardChange}
            onBlur={formik.handleBlur}
            brand={cardBrand}
            showLogo={true}
          />
          {formik.touched.cardNumber && formik.errors.cardNumber && (
            <p className="text-red-500 text-sm">{formik.errors.cardNumber}</p>
          )}
        </div>
        <div className="grid grid-cols-2 gap-3">
          <div>
            <FloatingLabelInput
              label="MM/YY"
              type="text"
              inputMode="numeric"
              value={formik.values.expirationDate}
              onChange={handleExpirationDateChange}
              onBlur={formik.handleBlur}
              name="expirationDate"
            />
            {formik.touched.expirationDate && formik.errors.expirationDate && (
              <p className="text-red-500 text-sm mt-1">
                {formik.errors.expirationDate}
              </p>
            )}
          </div>
          <div>
            <FloatingLabelInput
              label="CVV"
              type="text"
              inputMode="numeric"
              value={formik.values.cvv}
              onChange={handleCardChange}
              onBlur={formik.handleBlur}
              name="cvv"
            />
            {formik.touched.cvv && formik.errors.cvv && (
              <p className="text-red-500 text-sm mt-1">{formik.errors.cvv}</p>
            )}
          </div>
        </div>
        <div className="w-full flex gap-4">
          <Button
            type="submit"
            variant="primary"
            disabled={formik.isSubmitting || !formik.isValid}
          >
            {formik.isSubmitting ? (
              <Icon
                svg={Spinner}
                size={28}
                className="mx-auto text-white"
              />
            ) : (
              `Pay ${formatCurrency(payable.displayTotal / 100, 'NGN')}`
            )}
          </Button>
          <Button
            variant="outline"
            type="button"
            onClick={() =>
              window.parent.postMessage({ message: 'closeCeviantSDK' }, '*')
            }
            disabled={formik.isSubmitting}
          >
            Cancel
          </Button>
        </div>
      </form>
    </>
  );
};

export default CardComponent;
